import { useState, useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Button,
  Text,
  Card,
  Group,
  Badge,
  Loader,
  Center,
  Collapse,
  UnstyledButton,
  Modal,
  Select,
  Divider,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconPlus,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { api } from "../../lib";
import ErrorReportModal from "../grossing/ErrorReportModal";
import RichTextDiagnosis from "../pathologist-review/RichTextDiagnosis";
import { usePendingCorrectionsForCase } from "../../hooks/useCorrectionRequests";
import { sanitizeHtml } from "../../utils/sanitize";
import { errorRefLabel, correctionRefLabel } from "../../utils/formatters";

interface CaseError {
  id: string;
  refNumber?: number;
  discrepancy?: {
    error: string;
    description: string;
    category: string;
  };
  errorType?: {
    error: string;
    description: string;
    category: string;
  };
  description: string;
  blockCase: boolean;
  preventSignOff: boolean;
  status: "pending" | "resolved";
  resolvedAt?: string;
  resolvedBy?: {
    fullName: string;
  };
  resolutionNotes?: string;
  correctionRequestId?: string | null;
  correctionRequest?: { id?: string; refNumber?: number };
  reportedAt: string;
  reportedBy?: {
    fullName: string;
  };
  createdAt?: string;
  createdBy?: {
    fullName: string;
  };
}

interface ErrorsCardProps {
  caseId: string | null;
  suggestedErrorTypes?: string[];
  compact?: boolean;
}

const ERROR_DISPLAY_NAMES: Record<string, string> = {
  SpecNotRcvd: "Specimen Not Received",
  PatData: "Patient Data Missing",
  NoSpec: "No Specimen in Container",
};

function getErrorDisplayName(errorCode: string): string {
  return ERROR_DISPLAY_NAMES[errorCode] || errorCode;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString();
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

export default function ErrorsCard({
  caseId,
  suggestedErrorTypes = [],
  compact = false,
}: ErrorsCardProps) {
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedError, setSelectedError] = useState<CaseError | null>(null);
  const [detailModalOpened, setDetailModalOpened] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [linkedCorrectionId, setLinkedCorrectionId] = useState<string | null>(
    null
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: errors = [], isLoading } = useQuery({
    queryKey: ["caseErrors", caseId],
    queryFn: async () => {
      if (!caseId) return [];
      return api.get<CaseError[]>(`/workflows/errors/case/${caseId}`);
    },
    enabled: !!caseId,
  });

  const resolveMutation = useMutation({
    mutationFn: async ({
      errorId,
      resolutionNotes: notes,
      correctionRequestId,
    }: {
      errorId: string;
      resolutionNotes: string;
      correctionRequestId: string | null;
    }) => {
      return api.post(`/workflows/errors/${errorId}/resolve`, {
        resolutionNotes: notes,
        correctionRequestId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseErrors", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-embedding", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["grossing-queue"] });
      queryClient.invalidateQueries({ queryKey: ["embedding-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });

      setDetailModalOpened(false);
      setSelectedError(null);
      setResolutionNotes("");
      setLinkedCorrectionId(null);
      notifications.show({
        title: "Error Resolved",
        message: "The error has been marked as resolved",
        color: "green",
      });
    },
    onError: () => {
      notifications.show({
        title: "Error",
        message: "Failed to resolve error",
        color: "red",
      });
    },
  });

  const { data: pendingCorrectionsData } = usePendingCorrectionsForCase(
    caseId ?? undefined,
    detailModalOpened && selectedError?.status === "pending"
  );

  const correctionOptions = useMemo(
    () =>
      (pendingCorrectionsData?.correctionRequests ?? []).map((cr) => ({
        value: cr.id,
        label: `${
          correctionRefLabel(cr.refNumber)
            ? `${correctionRefLabel(cr.refNumber)} — `
            : ""
        }${cr.requestor.name ?? "Unknown"} · ${
          Object.keys(cr.proposedChanges ?? {}).length
        } field change(s) · ${new Date(cr.requestedAt).toLocaleDateString()}`,
      })),
    [pendingCorrectionsData]
  );

  const unresolvedErrors = useMemo(
    () => errors.filter((e) => e.status === "pending"),
    [errors]
  );

  const resolvedErrors = useMemo(
    () => errors.filter((e) => e.status === "resolved"),
    [errors]
  );

  const canCollapse = useMemo(() => errors.length > 0, [errors]);

  const effectiveExpanded = useMemo(
    () => (canCollapse ? isExpanded : true),
    [canCollapse, isExpanded]
  );

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpened(false);
    setSelectedError(null);
    setResolutionNotes("");
    setLinkedCorrectionId(null);
  }, []);

  const handleErrorClick = useCallback((error: CaseError) => {
    setSelectedError(error);
    setResolutionNotes("");
    setLinkedCorrectionId(null);
    setDetailModalOpened(true);
  }, []);

  const handleResolve = useCallback(() => {
    if (selectedError) {
      resolveMutation.mutate({
        errorId: selectedError.id,
        resolutionNotes,
        correctionRequestId: linkedCorrectionId,
      });
    }
  }, [selectedError, resolveMutation, resolutionNotes, linkedCorrectionId]);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleExpandErrors = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleOpenErrorModal = useCallback(() => {
    setErrorModalOpened(true);
  }, []);

  const handleCloseErrorModal = useCallback(() => {
    setErrorModalOpened(false);
  }, []);

  const cardContent = (
    <Stack gap="md">
      {!compact &&
        (canCollapse ? (
          <UnstyledButton
            onClick={handleToggleExpanded}
            style={{ width: "100%" }}
          >
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Stack gap={4}>
                <Group gap="xs" wrap="nowrap">
                  <IconAlertTriangle
                    size={16}
                    color={
                      unresolvedErrors.length > 0
                        ? "var(--mantine-color-red-6)"
                        : "var(--mantine-color-gray-6)"
                    }
                  />
                  <Text size="sm" fw={500}>
                    Reported Errors
                  </Text>
                </Group>
                {unresolvedErrors.length > 0 && (
                  <Badge color="red" variant="filled" size="sm">
                    {unresolvedErrors.length} unresolved
                  </Badge>
                )}
                {unresolvedErrors.length === 0 && resolvedErrors.length > 0 && (
                  <Badge color="green" variant="light" size="sm">
                    {resolvedErrors.length} resolved
                  </Badge>
                )}
              </Stack>
              {effectiveExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>
          </UnstyledButton>
        ) : (
          <Group gap="xs">
            <IconAlertTriangle size={16} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500}>
              Errors
            </Text>
          </Group>
        ))}

      {!effectiveExpanded && unresolvedErrors.length > 0 && (
        <UnstyledButton onClick={handleExpandErrors} style={{ width: "100%" }}>
          <Card withBorder p="xs" bg="red.0">
            <Group gap="xs" wrap="nowrap">
              <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
              <Text size="xs" lineClamp={1}>
                {unresolvedErrors[0].discrepancy?.error ||
                unresolvedErrors[0].errorType?.error
                  ? getErrorDisplayName(
                      unresolvedErrors[0].discrepancy?.error ||
                        unresolvedErrors[0].errorType?.error ||
                        ""
                    )
                  : unresolvedErrors[0].description}
                {unresolvedErrors.length > 1 &&
                  ` (+${unresolvedErrors.length - 1} more)`}
              </Text>
            </Group>
          </Card>
        </UnstyledButton>
      )}

      <Collapse in={effectiveExpanded}>
        <Stack gap="md">
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleOpenErrorModal}
            variant="outline"
            color="orange"
            fullWidth
            disabled={!caseId}
          >
            Report Error
          </Button>

          {!caseId ? (
            <Text c="dimmed" size="sm" ta="center">
              Save the case first to report errors
            </Text>
          ) : isLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : errors.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center">
              No errors reported
            </Text>
          ) : (
            <Stack gap="xs">
              {unresolvedErrors.map((error) => (
                <Card
                  key={error.id}
                  withBorder
                  p="xs"
                  bg="red.0"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleErrorClick(error)}
                >
                  <Stack gap={4}>
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap">
                        {errorRefLabel(error.refNumber) && (
                          <Badge size="xs" variant="outline" color="blue">
                            {errorRefLabel(error.refNumber)}
                          </Badge>
                        )}
                        <Text size="sm" fw={500} lineClamp={1}>
                          {error.discrepancy?.error || error.errorType?.error
                            ? getErrorDisplayName(
                                error.discrepancy?.error ||
                                  error.errorType?.error ||
                                  ""
                              )
                            : error.description || "Unknown Error"}
                        </Text>
                      </Group>
                      {error.blockCase && (
                        <Badge size="xs" color="red">
                          Blocked
                        </Badge>
                      )}
                    </Group>
                    <div
                      style={{
                        fontSize: "var(--mantine-font-size-xs)",
                        color: "var(--mantine-color-dimmed)",
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(error.description || ""),
                      }}
                    />
                    <Text size="xs" c="dimmed">
                      {error.reportedBy?.fullName || error.createdBy?.fullName}{" "}
                      • {formatDate(error.reportedAt || error.createdAt || "")}
                    </Text>
                  </Stack>
                </Card>
              ))}

              {resolvedErrors.length > 0 && (
                <>
                  <Text size="xs" c="dimmed" mt="xs">
                    Resolved ({resolvedErrors.length})
                  </Text>
                  {resolvedErrors.map((error) => (
                    <Card
                      key={error.id}
                      withBorder
                      p="xs"
                      bg="gray.0"
                      style={{ opacity: 0.7 }}
                    >
                      <Stack gap={4}>
                        <Group gap="xs" wrap="nowrap">
                          {errorRefLabel(error.refNumber) && (
                            <Badge size="xs" variant="outline" color="blue">
                              {errorRefLabel(error.refNumber)}
                            </Badge>
                          )}
                          <Text size="sm" fw={500} lineClamp={1}>
                            {error.discrepancy?.error || error.errorType?.error
                              ? getErrorDisplayName(
                                  error.discrepancy?.error ||
                                    error.errorType?.error ||
                                    ""
                                )
                              : error.description || "Unknown Error"}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          Resolved by {error.resolvedBy?.fullName} on{" "}
                          {formatDate(
                            error.resolvedAt || error.reportedAt || ""
                          )}
                        </Text>
                      </Stack>
                    </Card>
                  ))}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );

  return (
    <>
      {compact ? (
        cardContent
      ) : (
        <Paper p="sm" withBorder>
          {cardContent}
        </Paper>
      )}

      {caseId && (
        <ErrorReportModal
          opened={errorModalOpened}
          onClose={handleCloseErrorModal}
          caseId={caseId}
          suggestedErrorTypes={suggestedErrorTypes}
        />
      )}

      <Modal.Root
        opened={detailModalOpened}
        onClose={handleCloseDetailModal}
        size="xl"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              <Group gap="xs">
                {selectedError?.status === "resolved" ? (
                  <IconCheck size={20} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconAlertTriangle
                    size={20}
                    color="var(--mantine-color-red-6)"
                  />
                )}
                <Text fw={600}>Error Details</Text>
                {errorRefLabel(selectedError?.refNumber) && (
                  <Badge size="sm" variant="outline" color="blue">
                    {errorRefLabel(selectedError?.refNumber)}
                  </Badge>
                )}
              </Group>
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            {selectedError && (
              <Stack gap="md">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Error Type
                  </Text>
                  <Text size="sm" fw={500}>
                    {selectedError.discrepancy?.error ||
                    selectedError.errorType?.error
                      ? getErrorDisplayName(
                          selectedError.discrepancy?.error ||
                            selectedError.errorType?.error ||
                            ""
                        )
                      : "Unknown Error"}
                  </Text>
                  {(selectedError.discrepancy?.description ||
                    selectedError.errorType?.description) && (
                    <Text size="xs" c="dimmed">
                      {selectedError.discrepancy?.description ||
                        selectedError.errorType?.description}
                    </Text>
                  )}
                </div>

                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Description
                  </Text>
                  <div
                    style={{
                      fontSize: "var(--mantine-font-size-sm)",
                      wordBreak: "break-word",
                    }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(selectedError.description || ""),
                    }}
                  />
                </div>

                <Group gap="xs">
                  {selectedError.blockCase && (
                    <Badge color="red" variant="filled">
                      Blocks Case
                    </Badge>
                  )}
                  {selectedError.preventSignOff && (
                    <Badge color="orange" variant="filled">
                      Prevents Sign-Off
                    </Badge>
                  )}
                </Group>

                <Divider />

                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Reported By
                  </Text>
                  <Text size="sm">
                    {selectedError.reportedBy?.fullName ||
                      selectedError.createdBy?.fullName ||
                      "Unknown"}{" "}
                    on{" "}
                    {formatDateTime(
                      selectedError.reportedAt || selectedError.createdAt || ""
                    )}
                  </Text>
                </div>

                {selectedError.status === "resolved" && (
                  <>
                    <Divider />
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Resolved By
                      </Text>
                      <Text size="sm">
                        {selectedError.resolvedBy?.fullName || "Unknown"} on{" "}
                        {formatDateTime(selectedError.resolvedAt || "")}
                      </Text>
                    </div>
                    {selectedError.resolutionNotes && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Resolution Notes
                        </Text>
                        <div
                          style={{
                            fontSize: "var(--mantine-font-size-sm)",
                            wordBreak: "break-word",
                          }}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              selectedError.resolutionNotes || ""
                            ),
                          }}
                        />
                      </div>
                    )}
                    {selectedError.correctionRequestId && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Linked Correction
                        </Text>
                        <Button
                          variant="subtle"
                          size="xs"
                          mt={4}
                          onClick={() =>
                            navigate(`/cases/${caseId}?tab=corrections`)
                          }
                        >
                          {selectedError.correctionRequest?.refNumber != null
                            ? `View Linked Correction C${selectedError.correctionRequest.refNumber}`
                            : "View Linked Correction"}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {selectedError.status === "pending" && (
                  <>
                    <Divider />
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Resolution Notes
                      </Text>
                      <RichTextDiagnosis
                        value={resolutionNotes}
                        onChange={setResolutionNotes}
                        placeholder="Enter resolution notes..."
                      />
                    </div>
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Link Correction (Optional)
                      </Text>
                      <Select
                        placeholder={
                          correctionOptions.length === 0
                            ? "No pending corrections for this case"
                            : "Search corrections for this case..."
                        }
                        searchable
                        clearable
                        data={correctionOptions}
                        value={linkedCorrectionId}
                        onChange={setLinkedCorrectionId}
                        disabled={correctionOptions.length === 0}
                        nothingFoundMessage="No results"
                      />
                    </div>
                  </>
                )}
              </Stack>
            )}
          </Modal.Body>

          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={handleCloseDetailModal}>
              {selectedError?.status === "resolved" ? "Close" : "Cancel"}
            </Button>
            {selectedError?.status === "pending" && (
              <Button
                color="green"
                onClick={handleResolve}
                loading={resolveMutation.isPending}
              >
                Mark as Resolved
              </Button>
            )}
          </Group>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
